import React, { useCallback, useEffect, useRef } from "react";

// Ab dieser horizontalen Distanz gilt eine Touch-Geste als Wisch.
const SWIPE_THRESHOLD = 50;

/**
 * Leichtgewichtige Lightbox ohne jQuery-Abhängigkeit.
 * Ersetzt nivo-lightbox, dessen JS nie eingebunden war (Klick auf ein
 * Galeriebild hat die Seite verlassen statt eine Ansicht zu öffnen).
 */
export const Lightbox = ({ items, index, onClose, onNavigate }) => {
  const closeRef = useRef(null);
  const touchStart = useRef(null);
  const isOpen = index !== null && index >= 0 && index < items.length;

  const next = useCallback(
    () => onNavigate((index + 1) % items.length),
    [index, items.length, onNavigate]
  );
  const prev = useCallback(
    () => onNavigate((index - 1 + items.length) % items.length),
    [index, items.length, onNavigate]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };

    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    if (closeRef.current) closeRef.current.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose, next, prev]);

  // Nachbarbilder vorladen, damit Wischen/Blättern ohne Ladepause wirkt.
  useEffect(() => {
    if (!isOpen || items.length < 2) return;
    [(index + 1) % items.length, (index - 1 + items.length) % items.length].forEach(
      (i) => {
        const img = new window.Image();
        img.src = items[i].largeImage;
      }
    );
  }, [isOpen, index, items]);

  if (!isOpen) return null;

  const current = items[index];

  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;
    // Nur waagerechte Gesten auswerten, sonst kollidiert es mit Scrollen.
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    dx < 0 ? next() : prev();
  };

  return (
    <div
      className="lb-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={current.title || "Galeriebild"}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        ref={closeRef}
        type="button"
        className="lb-btn lb-close"
        aria-label="Schließen"
        onClick={onClose}
      >
        &times;
      </button>

      {items.length > 1 && (
        <button
          type="button"
          className="lb-btn lb-prev"
          aria-label="Vorheriges Bild"
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
        >
          &#8249;
        </button>
      )}

      <figure className="lb-figure" onClick={(e) => e.stopPropagation()}>
        <img src={current.largeImage} alt={current.title || ""} />
        <figcaption>
          {current.title && (
            <span className="lb-title">{current.title}</span>
          )}
          <span className="lb-counter">
            {index + 1} / {items.length}
          </span>
        </figcaption>
      </figure>

      {items.length > 1 && (
        <button
          type="button"
          className="lb-btn lb-next"
          aria-label="Nächstes Bild"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
        >
          &#8250;
        </button>
      )}
    </div>
  );
};
